import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CaretDown, Check, House, UsersThree, Plus } from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth";

const ROLE_LABEL = {
  owner: "Owner",
  editor: "Editor",
  viewer: "Viewer",
  executor: "Executor",
};

export default function LedgerSwitcher() {
  const {
    households,
    activeLedgerId,
    activeLedgerLabel,
    activeRole,
    setActiveLedger,
  } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const isPersonal = !activeLedgerId;
  // v3.15 — hide the "Personal / Private ledger" option whenever the
  // user has at least one household. In testing, keeping it visible
  // caused confusion: no one used it for assets and it doubled the
  // switcher's mental model. It remains reachable in code (setActiveLedger(null))
  // so any legacy assets still on the personal ledger are recoverable
  // from Settings → Legacy assets if we ever add that flow.
  const showPersonal = households.length === 0;

  return (
    <div ref={ref} className="relative" data-testid="ledger-switcher">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="ledger-switcher-btn"
        className="inline-flex items-center gap-2 bg-white border border-[#E0DDD6] hover:border-[#1A3622] px-3 py-1.5 text-sm text-[#0A192F] transition-colors"
      >
        <span className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] hidden sm:inline">
          Active Ledger:
        </span>
        <span className="font-medium max-w-[160px] truncate">{activeLedgerLabel}</span>
        <CaretDown size={12} />
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-72 bg-white border border-[#E0DDD6] shadow-sm z-50"
          data-testid="ledger-switcher-menu"
        >
          <div className="px-4 py-3 border-b border-[#E0DDD6]">
            <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58]">
              Switch Ledger
            </div>
          </div>

          {showPersonal && (
            <button
              onClick={() => {
                setActiveLedger(null);
                setOpen(false);
              }}
              data-testid="ledger-option-personal"
              className={`w-full text-left px-4 py-3 text-sm hover:bg-[#F9F8F6] flex items-center gap-3 border-b border-[#E0DDD6]/60 ${
                isPersonal ? "bg-[#F9F8F6]" : ""
              }`}
            >
              <House size={16} weight="regular" className="text-[#1A3622] shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-[#0A192F] font-medium">Personal</div>
                <div className="text-[10px] font-mono-ledger uppercase tracking-[0.25em] text-[#5C5F58]">
                  Owner · Private ledger
                </div>
              </div>
              {isPersonal && <Check size={14} weight="bold" className="text-[#1A3622]" />}
            </button>
          )}

          {households.length > 0 && (
            <div className="max-h-72 overflow-y-auto">
              {households.map((h) => {
                const active = h.id === activeLedgerId;
                return (
                  <button
                    key={h.id}
                    onClick={() => {
                      setActiveLedger(h.id);
                      setOpen(false);
                    }}
                    data-testid={`ledger-option-${h.id}`}
                    className={`w-full text-left px-4 py-3 text-sm hover:bg-[#F9F8F6] flex items-center gap-3 border-b border-[#E0DDD6]/60 ${
                      active ? "bg-[#F9F8F6]" : ""
                    }`}
                  >
                    <UsersThree size={16} weight="regular" className="text-[#1A3622] shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-[#0A192F] font-medium truncate">{h.name}</div>
                      <div className="text-[10px] font-mono-ledger uppercase tracking-[0.25em] text-[#5C5F58]">
                        {ROLE_LABEL[h.my_role] || h.my_role} · Household
                      </div>
                    </div>
                    {active && <Check size={14} weight="bold" className="text-[#1A3622]" />}
                  </button>
                );
              })}
            </div>
          )}

          <Link
            to="/households"
            onClick={() => setOpen(false)}
            data-testid="manage-households-link"
            className="block px-4 py-3 text-sm text-[#0A192F] hover:bg-[#F9F8F6] flex items-center gap-2 border-t border-[#E0DDD6]"
          >
            <Plus size={14} weight="bold" />
            Manage households
          </Link>

          {!!activeLedgerId && (
            <div className="px-4 py-2 text-[10px] font-mono-ledger uppercase tracking-[0.25em] text-[#5C5F58] bg-[#F9F8F6] border-t border-[#E0DDD6]">
              Your role: {ROLE_LABEL[activeRole] || activeRole}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
